import axios from 'axios';

// Usa URL relativi per funzionare con il proxy (supporta port forwarding)
const API_BASE_URL = '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor per aggiungere il token JWT a tutte le richieste
apiClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor per gestire errori di autenticazione
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token scaduto o non valido - rimuovi e forza logout
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('isAuthenticated');
      // Forza refresh della pagina per mostrare login
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (password) => {
    const response = await axios.post(`${API_BASE_URL}/auth/login`, { password });
    if (response.data.token) {
      sessionStorage.setItem('authToken', response.data.token);
      sessionStorage.setItem('isAuthenticated', 'true');
    }
    return response;
  },

  logout: () => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('isAuthenticated');
  },

  verify: async () => {
    const token = sessionStorage.getItem('authToken');
    if (!token) return { data: { valid: false } };

    try {
      const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response;
    } catch {
      return { data: { valid: false } };
    }
  },

  isAuthenticated: () => {
    return sessionStorage.getItem('isAuthenticated') === 'true' &&
      sessionStorage.getItem('authToken') !== null;
  }
};

// Preventivi API
export const preventiviApi = {
  getAll: () => apiClient.get('/preventivi'),

  getById: (id) => apiClient.get(`/preventivi/${id}`),

  create: (data) => apiClient.post('/preventivi', data),

  update: (id, data) => apiClient.put(`/preventivi/${id}`, data),

  delete: (id) => apiClient.delete(`/preventivi/${id}`),

  // Versioning API
  createNewVersion: (id) => apiClient.post(`/preventivi/${id}/nuova-versione`),

  getVersions: (id) => apiClient.get(`/preventivi/${id}/versioni`),

  getVersion: (id, versione) => apiClient.get(`/preventivi/${id}/versioni/${versione}`),

  restoreVersion: (id, versione) => apiClient.post(`/preventivi/${id}/versioni/${versione}/ripristina`),
};

// PDF API
export const pdfApi = {
  // Genera PDF e ottieni link di download temporaneo (valido 15 min)
  generate: (html, filename) =>
    apiClient.post('/pdf/generate', { html, filename }),
};

// AI Assistant API
export const aiApi = {
  // Chat con l'assistente AI
  chat: (messages, context = null) =>
    apiClient.post('/ai/chat', { messages, context }),

  // Riscrivi un testo specifico
  rewrite: (text, instruction, field = null) =>
    apiClient.post('/ai/rewrite', { text, instruction, field }),

  // Suggerisci un prezzo per un deliverable
  suggestPrice: (deliverable, context = null) =>
    apiClient.post('/ai/suggest-price', { deliverable, context }),
};

// Backup API
export const backupApi = {
  // Ottieni info storage
  getStorageInfo: () => apiClient.get('/storage/info'),

  // Esporta tutti i preventivi
  exportAll: () => apiClient.get('/backup/export'),

  // Importa preventivi
  importData: (data, replace = false) =>
    apiClient.post('/backup/import', { preventivi: data.preventivi || data, replace }),

  // Lista backup disponibili
  listBackups: () => apiClient.get('/backup/list'),

  // Download backup
  downloadBackup: (filename) =>
    apiClient.get(`/backup/download/${filename}`, { responseType: 'blob' }),
};

export default apiClient;
